import React, { useCallback } from 'react';
import { makeSortable } from './sortable.jsx';
import { Icon } from '@iconify-icon/react';
import { cn, addClasses, mergeClasses } from '#lib/helpers/class.js';
import { isDefined } from '#lib/utils/undefined.js';
import { Row, RowTitle, RowPlaceholder, RowButtons } from './row.jsx';
import FloatingTooltip from '#components/tooltip.jsx';
import Button from '#lib/components/button.jsx';
import { isFunction } from '#lib/utils/function.js';
import { SuggestionIcon } from '#components/suggestion-icon.jsx';
import { sameIdOrImportId, ungroup } from '#@itinerary/places/helpers.js';
import { checkInvalidSuggestionPlan } from './helpers.js';
import ImagePreview from '#components/images/image-preview.jsx';

function _OverviewCell(props, ref) {
    const {
        as = 'div',
        name,
        dayIndex,
        selected,
        onSelect,
        addDay,
        style,
        overlay = false,
        children,
        hasChild,
        className,
        title,
        ...day
    } = props;
    const dayNumber = dayIndex + 1;

    const onClick = React.useCallback(() => {
        if (!isDefined(dayIndex)) return;
        if (typeof onSelect === 'function') onSelect(dayIndex);
    }, [onSelect, dayIndex]);

    return (
        <Row
            ref={ref}
            as={as}
            className={addClasses(
                mergeClasses(
                    'relative col-span-full flex flex-col space-y-2 justify-between border-none mb-4',
                    className
                )
            )}
            selected={selected}
            style={{
                gridRowStart: dayNumber ? dayNumber : 0,
                zIndex: selected ? 10 : 0,
                ...style
            }}
            onClick={onClick}
        >
            <div
                className={cn(
                    'w-full px-1 py-1 flex justify-between items-center gap-x-4 border-b',
                    !dayNumber && 'cursor-pointer hover:text-primary-500'
                )}
                onClick={addDay}
            >
                <div className="w-full flex justify-start items-center gap-x-6 text-[#1a83b8]">
                    {dayNumber ? <span className="font-bold">{title}</span> : <span>{_t.edit.planner.newDay()}</span>}
                </div>
                {dayNumber ? (
                    <RowButtons overlay={overlay} hasChild={hasChild} {...props} />
                ) : (
                    <FloatingTooltip content={_t.edit.planner.addDay()}>
                        <Icon
                            className="cursor-pointer  text-primary-500 pt-[2px]"
                            icon="material-symbols-light:add"
                            width="1.6rem"
                            height="1.6rem"
                        />
                    </FloatingTooltip>
                )}
            </div>
            <div className="w-full flex">{children}</div>
        </Row>
    );
}

export const OverviewCell = React.forwardRef(_OverviewCell);
export const SortableOverviewCell = makeSortable(OverviewCell);

function _DiffOverviewCell(props, ref) {
    const {
        as = 'div',
        name,
        dayIndex,
        selected,
        onSelect,
        addDay,
        style,
        overlay = false,
        children,
        hasChild,
        className,
        ...day
    } = props;
    const dayNumber = dayIndex + 1;

    const onClick = React.useCallback(() => {
        if (!isDefined(dayIndex)) return;
        if (typeof onSelect === 'function') onSelect(dayIndex);
    }, [onSelect, dayIndex]);

    return (
        <Row
            ref={ref}
            as={as}
            className={addClasses(
                mergeClasses(
                    'relative col-span-full flex flex-col space-y-2 justify-between border-none mb-4',
                    className
                )
            )}
            onClick={onClick}
        >
            <div className="w-full px-1 py-1 flex justify-between items-center gap-x-4 border-b">
                <div className="w-full flex justify-start items-center gap-x-6">
                    {dayNumber ? (
                        <span className="font-bold">
                            {_t.edit.planner.dayName()} {dayNumber}
                        </span>
                    ) : (
                        <span>{_t.edit.planner.newDay()}</span>
                    )}
                </div>
            </div>
            <div className="w-full flex">{children}</div>
        </Row>
    );
}

export const DiffOverviewCell = React.forwardRef(_DiffOverviewCell);

function _PlanRow(props, ref) {
    const {
        as = 'li',
        selfId,
        name,
        type,
        selected,
        onSelect,
        style,
        isImg,
        src,
        overlay = false,
        suggestionId,
        places,
        diffExists,
        isIncoming,
        isUpdated,
        transportation,
        suggestionType
    } = props;

    const onClick = React.useCallback(() => {
        onSelect(selfId);
    }, [onSelect, selfId]);

    const suggestion = ungroup(places).find(
        (place) => place.type === 'suggestion' && sameIdOrImportId(place, suggestionId)
    );

    return (
        <Row
            as={as}
            ref={ref}
            style={{ zIndex: selected ? 10 : 0, ...style }}
            className={addClasses(
                cn(
                    'relative pl-2 pr-1 py-1 bg-white',
                    (diffExists || isUpdated) && 'bg-primary-100 bg-opacity-50 text-primary-500',
                    isIncoming ? 'text-neutral-500' : 'cursor-pointer'
                )
            )}
            selected={selected}
        >
            <div className="w-full flex justify-between items-center gap-x-6">
                <div
                    className={cn(
                        'w-full flex justify-start items-center gap-x-2',
                        isIncoming
                            ? 'text-neutral-500 pointer-events-none'
                            : selected
                            ? 'text-primary-600'
                            : 'hover:text-primary-600'
                    )}
                    onClick={onClick}
                >
                    <PlanIcon type={type} category={suggestionType} transportation={transportation} />
                    {isImg && <img src={src} alt={name} className="w-16 h-16 rounded mr-2 object-cover" />}
                    {name && (
                        <RowTitle>
                            {type === 'suggestion' && suggestion && Object.keys(suggestion).length
                                ? suggestion.name
                                : name}
                            {isIncoming && (
                                <span className="pl-2 text-sm">{`[${_t.edit.updateItinerary.justAddedPlan()}]`}</span>
                            )}
                            {isUpdated && (
                                <span className="pl-2 text-sm">{`[${_t.edit.updateItinerary.updatedPlan()}]`}</span>
                            )}
                            {diffExists && (
                                <span className="pl-2 text-sm">{`[${_t.edit.updateItinerary.planToUpdate()}]`}</span>
                            )}
                        </RowTitle>
                    )}
                    {!name && <RowPlaceholder>{type}</RowPlaceholder>}
                </div>
                <RowButtons overlay={overlay} {...props} />
            </div>
        </Row>
    );
}

function _DiffPlanRow(props, ref) {
    const {
        as = 'li',
        selfId,
        importId,
        name,
        type,
        onSelect,
        transportation,
        incoming,
        deleted,
        wrongDay,
        current,
        previewMode,
        suggestionType,
        hoveredPlanImportId,
        handleHoveredPlan,
        suggestionId,
        updatedPlaces,
        incomingPlaces
    } = props;

    const suggestion = updatedPlaces?.find((place) => sameIdOrImportId(place, suggestionId));
    const suggestionToAdd = incomingPlaces?.find((place) => sameIdOrImportId(place, suggestionId) && !suggestion);
    const isInvalidSuggestion = checkInvalidSuggestionPlan(type, previewMode, suggestion);

    const onClick = useCallback(() => {
        if (checkInvalidSuggestionPlan(type, previewMode, suggestion)) return;
        if (typeof onSelect === 'function') onSelect(selfId);
    }, [onSelect, selfId, suggestion, type]);

    return (
        <Row
            as={as}
            ref={ref}
            className={addClasses(
                cn(
                    'relative pl-2 pr-1 py-1 my-1',
                    incoming || (deleted && 'text-red-500 bg-red-50'),
                    wrongDay && !current && 'transition-all text-primary-500',
                    importId && hoveredPlanImportId === importId && 'bg-primary-100 bg-opacity-50 text-primary-500',
                    isInvalidSuggestion && incoming && 'bg-red-100 text-red-400'
                )
            )}
            onMouseOver={() => {
                if (isFunction(handleHoveredPlan)) handleHoveredPlan(importId);
            }}
            onMouseOut={() => {
                if (isFunction(handleHoveredPlan)) handleHoveredPlan(null);
            }}
        >
            <div className="w-full flex justify-between items-center gap-x-6">
                <div className="w-full flex justify-start items-center gap-x-2" onClick={onClick}>
                    <PlanIcon type={type} category={suggestion?.filterName} transportation={transportation} />

                    <RowTitle>
                        {type === 'suggestion' &&
                            previewMode == 'embed' &&
                            (suggestion ? suggestion?.name : suggestionToAdd?.name)}
                        {type === 'suggestion' && previewMode == 'modal' && suggestionType}
                        {type !== 'suggestion' && name}
                        {isInvalidSuggestion && incoming && (
                            <span className="pl-2 text-sm">{`[${_t.edit.updateItinerary.placeToAdd()}]`}</span>
                        )}
                    </RowTitle>

                    {!name && <RowPlaceholder>{type}</RowPlaceholder>}
                </div>
                <IncomingButtons {...props} suggestion={suggestion} />
            </div>
        </Row>
    );
}

function _ImageRow(props, ref) {
    const { as = 'li', name, type, selected, onEdit, style, src, coords, overlay = false } = props;

    return (
        <Row
            as={as}
            ref={ref}
            style={{ zIndex: selected ? 10 : 0, ...style }}
            className="flex items-center gap-1 -mt-px relative pl-2 pr-1 py-1 my-1 cursor-pointer"
            selected={selected}
        >
            <div className="w-full flex justify-between items-center gap-x-6">
                <div className="w-full flex justify-start items-center gap-x-2">
                    <div className="w-16 aspect-video rounded overflow-hidden mr-2">
                        <ImagePreview imageSrc={src} alt={name} initialState={coords} />
                    </div>
                    {name ? <RowTitle>{name}</RowTitle> : <RowPlaceholder>{type}</RowPlaceholder>}
                </div>
                <RowButtons overlay={overlay} edit={true} onEdit={onEdit} {...props} />
            </div>
        </Row>
    );
}

export const ImageRow = React.forwardRef(_ImageRow);
export const SortableImageRow = makeSortable(ImageRow);

export const PlanRow = React.forwardRef(_PlanRow);
export const SortablePlanRow = makeSortable(PlanRow);

export const DiffPlanRow = React.forwardRef(_DiffPlanRow);

// SubComponents

export function PlanIcon(props) {
    const { category, type, transportation } = props;

    let icon = 'bx:trip';
    if (transportation && type === 'transfer')
        switch (transportation) {
            case 'C':
                icon = 'ri:car-line';
                break;
            case 'S':
                icon = 'ri:ship-2-line';
                break;
            case 'F':
                icon = 'tabler:plane-tilt';
                break;
            case 'T':
                icon = 'material-symbols-light:train-outline';
                break;
            default:
        }
    return (
        <>
            {type === 'activity' && (
                <FloatingTooltip content={_t.edit.planner.activity()} placement="top">
                    <Icon
                        icon="material-symbols:calendar-month-outline-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className="pt-[2px]"
                    />
                </FloatingTooltip>
            )}
            {type === 'accommodation' && (
                <FloatingTooltip content={_t.edit.planner.accommodation()} placement="top">
                    <Icon
                        icon="material-symbols:hotel-outline-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className="pt-[4px]"
                    />
                </FloatingTooltip>
            )}
            {type === 'transfer' && (
                <FloatingTooltip content={_t.edit.planner.transfer()} placement="top">
                    <Icon icon={icon} width="1.2rem" height="1.2rem" className="pt-[4px]" />
                </FloatingTooltip>
            )}
            {type === 'custom' && (
                <FloatingTooltip content={_t.edit.planner.custom()} placement="top">
                    <Icon
                        icon="material-symbols:inbox-customize-outline-rounded"
                        width="1.2rem"
                        height="1.2rem"
                        className="pt-[3px]"
                    />
                </FloatingTooltip>
            )}
            {type === 'suggestion' && (
                <FloatingTooltip content={_t.edit.planner.suggestion()} placement="top">
                    <SuggestionIcon category={category} className="mt-2" />
                </FloatingTooltip>
            )}
            {type === 'info' && (
                <FloatingTooltip content={_t.edit.planner.info()} placement="top">
                    <Icon icon="material-symbols:info-outline-rounded" className="mt-2" />
                </FloatingTooltip>
            )}
        </>
    );
}

function IncomingButtons(props) {
    const {
        addIncomingPlan,
        removePlan,
        incoming,
        deleted,
        wrongDay,
        current,
        updated,
        reorderPlan,
        restoreCurrent,
        suggestion,
        type,
        previewMode
    } = props;

    return (
        <>
            {incoming && (
                <Button
                    className={cn(
                        'text-neutral-700 hover:text-primary-500 pr-2',
                        checkInvalidSuggestionPlan(type, previewMode, suggestion) &&
                            'text-neutral-400 hover:text-neutral-400'
                    )}
                    onClick={() =>
                        checkInvalidSuggestionPlan(type, previewMode, suggestion) ? null : addIncomingPlan()
                    }
                >
                    <Icon className="cursor-pointer pt-[2px]" icon="ic:round-add" width="1.3rem" height="1.3rem" />
                </Button>
            )}
            {!incoming && !current && (
                <Button
                    className={cn(
                        'text-neutral-700 hover:text-primary-500 pr-2',
                        checkInvalidSuggestionPlan(type, previewMode, suggestion) &&
                            'text-neutral-400 hover:text-neutral-400'
                    )}
                    onClick={() => (checkInvalidSuggestionPlan(type, previewMode, suggestion) ? null : removePlan())}
                >
                    <Icon icon="ic:round-remove" className="cursor-pointer p-[2px]" width="1.2rem" height="1.2rem" />
                </Button>
            )}
            {deleted && (
                <Button
                    className={cn(
                        'text-neutral-700 hover:text-red-500 pr-2',
                        checkInvalidSuggestionPlan(type, previewMode, suggestion) &&
                            'text-neutral-400 hover:text-neutral-400'
                    )}
                    onClick={() => (checkInvalidSuggestionPlan(type, previewMode, suggestion) ? null : removePlan())}
                >
                    <Icon icon="ic:round-remove" className="cursor-pointer p-[2px]" width="1.2rem" height="1.2rem" />
                </Button>
            )}
            {wrongDay && !current && (
                <FloatingTooltip content={_t.edit.updateItinerary.reorderTooltipText()} placement="top">
                    <Button className="text-neutral-700 hover:text-primary-500 pr-2" onClick={reorderPlan}>
                        <Icon
                            icon="tabler:reorder"
                            className="cursor-pointer p-[2px] rotate-90"
                            width="1.2rem"
                            height="1.2rem"
                        />
                    </Button>
                </FloatingTooltip>
            )}
            {updated && current && (
                <FloatingTooltip content={_t.edit.updateItinerary.restoreTooltipText()} placement="top">
                    <Button className="text-neutral-700 hover:text-primary-500 pr-2" onClick={restoreCurrent}>
                        <Icon
                            icon="tabler:reorder"
                            className="cursor-pointer p-[2px] rotate-90"
                            width="1.2rem"
                            height="1.2rem"
                        />
                    </Button>
                </FloatingTooltip>
            )}
        </>
    );
}
